'use client'
import { useEffect, useState } from 'react'
import logger from '@achieve/cx-logger'
import Cookies from 'js-cookie'
import { usePathname } from 'next/navigation'
import { ACX_CHATBOT, FDR_CHATBOT, CLIENT_SIDE_FEATURE_TOGGLES } from '@/constants'
import SITE_ENVIRONMENTS from '@/constants/websites'
import { useSearchParams } from 'next/navigation'

const getChatFlag = (siteEnv) => (siteEnv === SITE_ENVIRONMENTS.FDR ? FDR_CHATBOT : ACX_CHATBOT)

const COOKIE_POLL_INTERVAL_MS = 500
const COOKIE_POLL_MAX_ATTEMPTS = 20

const ACHIEVE_ALLOWED_PAGES = [
  '/home-equity-loan',
  '/heloc',
  '/personal-loans',
  '/debt-consolidation',
  '/debt-relief',
  '/acceleration-loan',
  '/good-app',
  '/molo-app',
  '/smarter-debt',
]

export default function ChatGate({ siteEnv, isReturningUser, onFlagReady }) {
  const [browserId, setBrowserId] = useState(() => Cookies.get('browser_id') ?? null)
  const [visitId, setVisitId] = useState(() => Cookies.get('eh_visit_id') ?? null)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const utmSource = searchParams.get('utm_source')

  // Cookies may be set asynchronously by edge workers / Event Horizon SDK.
  // Poll until both are available so the main effect can self-trigger.
  useEffect(() => {
    if (browserId && visitId) return

    let attempts = 0
    const id = setInterval(() => {
      attempts++
      const bid = Cookies.get('browser_id')
      const vid = Cookies.get('eh_visit_id')
      if (bid && !browserId) setBrowserId(bid)
      if (vid && !visitId) setVisitId(vid)
      if ((bid || browserId) && (vid || visitId)) {
        clearInterval(id)
      } else if (attempts >= COOKIE_POLL_MAX_ATTEMPTS) {
        clearInterval(id)
      }
    }, COOKIE_POLL_INTERVAL_MS)

    return () => clearInterval(id)
  }, [browserId, visitId])

  useEffect(() => {
    let cancelled = false

    if (!browserId || !visitId) {
      return
    }

    if (typeof isReturningUser !== 'boolean') {
      return
    }

    if (isReturningUser) {
      onFlagReady?.('Off')
      return
    }

    if (!CLIENT_SIDE_FEATURE_TOGGLES[siteEnv]?.includes(getChatFlag(siteEnv))) {
      onFlagReady?.('Off')
      return
    }

    if (siteEnv === SITE_ENVIRONMENTS.ACHIEVE && !ACHIEVE_ALLOWED_PAGES.includes(pathname)) {
      onFlagReady?.('Off')
      return
    }

    async function fetchVariation() {
      try {
        const res = await fetch('/api/chatbot-experiment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            browserId,
            visitId,
            userType: 'new',
            siteEnv,
            ...(utmSource && { utmSource }),
          }),
        })

        if (!cancelled) {
          const { variation = 'Off' } = await res.json()
          onFlagReady?.(variation)
        }
      } catch (error) {
        logger.error('[ChatGate] Fetch error', { error })
        if (!cancelled) onFlagReady?.('Off')
      }
    }

    void fetchVariation()

    return () => {
      cancelled = true
    }
  }, [siteEnv, pathname, browserId, visitId, isReturningUser, onFlagReady, utmSource])

  return null
}
